import { gql, useQuery } from '@apollo/client'
import { useMemo } from 'react'

import { getDateRangeLabelV2 } from 'pared/utils/date'

import { useGroupFilter } from '../../../groupFilter'
import format from '../../../utils/format'
import { useVariables } from '../../../variables'

type IMetricDataType = Record<
  string,
  {
    name: string
    unit: 'CENT' | 'PERCENTAGE' | 'DOLLAR' | 'COUNT'
    value: number
  }
>

type INodeType = Parameters<typeof getDateRangeLabelV2>[1] & {
  locationGroupId: number
  startDate: string
  endDate: string
  metricSummaryData: IMetricDataType
}

interface IDataType {
  trendLocationGroupMetricValuesByDateRangeNumber: {
    nodes: INodeType[]
  }
  customizedDateGroupDelivery: {
    nodes: {
      startDate: string
      endDate: string
      totalOrderCount: number
    }[]
  }
}

const query = gql`
  query customTrendDelivery(
    $iEndDate: Date!
    $iDateRangeType: String!
    $iLocationGroupIds: [Int]!
  ) {
    trendLocationGroupMetricValuesByDateRangeNumber(
      iEndDate: $iEndDate
      iGroupBy: $iDateRangeType
      iFilter: {
        location_group_ids: $iLocationGroupIds
        metrics: [
          "delivery_total_order_count_doordash"
          "delivery_order_with_any_issue_percent_doordash"
          "delivery_inaccurate_order_percent_doordash"
          "delivery_cancelled_order_percent_doordash"
          "delivery_delayed_order_percent_doordash"
        ]
      }
    ) {
      nodes {
        locationGroupId
        groupByStartDate: startDate
        groupByEndDate: endDate
        businessYear
        businessQuarter
        businessWeek
        businessWeekOfMonth
        businessMonth
        metricSummaryData
      }
    }

    customizedDateGroupDelivery(
      iEndDate: $iEndDate
      iDateRangeType: $iDateRangeType
      iFilter: { location_group_ids: $iLocationGroupIds }
    ) {
      nodes {
        startDate
        endDate
        totalOrderCount
      }
    }
  }
`

export const bbbTrendLocationGroupDeliveryConfigs = {
  date: 'string',
  totalOrderCount: 'number',
  deliveryOrderWithAnyIssuePercentDoordash: 'percent',
  deliveryInaccurateOrderPercentDoordash: 'percent',
  deliveryCancelledOrderPercentDoordash: 'percent',
  deliveryDelayedOrderPercentDoordash: 'percent',
} as const

const useBbbTrendLocationGroupDelivery = () => {
  const { variables } = useVariables()
  const dateRange = useMemo(() => {
    switch (variables.date?.value.type) {
      case 'year':
        return 'business_year'
      case 'quarter':
        return 'business_quarter'
      case 'period':
        return 'business_month'
      case 'week':
        return 'business_week'
      default:
        return 'bussiness_week'
    }
  }, [variables])
  const { groupFilter } = useGroupFilter()
  const { data, loading } = useQuery<IDataType>(query, {
    variables: {
      iEndDate: variables.date?.value.dateRange.endDateStr,
      iDateRangeType: dateRange,
      iLocationGroupIds: groupFilter?.ids,
    },
    skip: !variables.date || !groupFilter,
  })

  return {
    data: useMemo(() => {
      if (!data) return null

      const trendData =
        data.trendLocationGroupMetricValuesByDateRangeNumber.nodes
      const customData = data.customizedDateGroupDelivery.nodes

      return trendData
        .filter((d) => d.metricSummaryData)
        .map((d) => {
          const customD = customData.find(
            (c) => c.startDate === d.groupByStartDate,
          )

          return {
            ...format(d.metricSummaryData),
            totalOrderCount: customD?.totalOrderCount,
            date: getDateRangeLabelV2(dateRange, d),
          }
        })
    }, [data, dateRange]),
    loading,
  }
}

export default useBbbTrendLocationGroupDelivery
